/**
 * The sentences the bulk Review Lot & NDC screen says about the BATCH.
 *
 * Split from bulkLotNdcStatus, which answers the other question — what is true of one ROW. They
 * were one file until POM-6121 pushed it past the length cap, and the seam was already there:
 * nothing here reads a row's fields except to count and name what is being held back.
 */

import { ROW_STATUS, UNUSABLE_REASON } from './bulkLotNdcStatus';

const EXCLUDED_REASON = {
  [ROW_STATUS.UNUSABLE_LOT]: 'reserved lot expired',
  [ROW_STATUS.NO_NDC]: 'no NDC assigned',
  // POM-6121 — these block only while the switch is on, but the panel has to name them when they
  // do. Without a word here they fell through to 'needs a decision', which says nothing about
  // what is wrong and nothing about how to fix it.
  [ROW_STATUS.NO_CATALOG]: 'no inventory for this product here',
  [ROW_STATUS.NO_LOT]: 'no lot recorded',
  [ROW_STATUS.NO_USABLE_LOT]: 'no usable lot in stock',
};

/**
 * The distinct reasons the batch is holding rows back, e.g. 'reserved lot expired'.
 *
 * Deduped rather than listed per row: a batch with eleven expired lots should say "reserved lot
 * expired" once, not eleven times. Only blocking rows count — an informational row that prints
 * without a lot number is not being held back and must not appear here.
 */
export const excludedReasonSummary = (rows) => {
  const reasons = (rows ?? [])
    .filter((row) => row.blocksBatch)
    .map((row) => {
      if (row.status === ROW_STATUS.UNUSABLE_LOT) {
        // POM-6119: a short lot is neither expired nor gone, and the panel naming it either way
        // sends the operator looking for a replacement when the lot in their hand is the answer.
        if (row.unusableReason === UNUSABLE_REASON.SHORT_FOR_FILL) {
          return 'lot holds less than the fill quantity';
        }

        if (row.unusableReason !== UNUSABLE_REASON.EXPIRED) {
          return 'reserved lot no longer available';
        }
      }

      return EXCLUDED_REASON[row.status] ?? 'needs a decision';
    });

  return [...new Set(reasons)].join(' · ');
};

/**
 * The banner's sentence about the listed rows.
 *
 * Exists because "listed" and "needs a decision" are different things and the banner used to
 * conflate them: every visible row was announced as a decision, so a batch whose only listed row
 * was read-only told the user to act and then offered no control. Splitting the two is the whole
 * point of this function, which is why it takes the counts rather than the rows — the classifier
 * decides what is actionable, this only phrases it.
 *
 * `excluded` is the checkbox under the grid, for the same reason printableSummary takes it: what
 * a blocked row DOES is owned by that toggle, not by its status. Without it this sentence kept
 * saying those rows "still print as they are" while the sentence directly before it said none of
 * them would print — the screen contradicting itself twice in one banner, which is what a batch of
 * four expired lots showed on open.
 */
export const listedRowsSummary = ({
  listed,
  actionable,
  blocked,
  informational,
  withoutLot,
  excluded = false,
}) => {
  if (listed === 0) return 'Nothing needs a decision.';

  const parts = [];

  // What the blocked rows are actually doing, which the toggle decides and the status does not.
  const blockedFate = excluded
    ? 'being left out of this batch'
    : `that still ${blocked === 1 ? 'prints' : 'print'} as ${blocked === 1 ? 'it is' : 'they are'}`;

  if (actionable > 0) {
    parts.push(
      `${actionable} need${actionable === 1 ? 's' : ''} a decision${
        blocked > 0 ? `, including ${blocked} ${blockedFate}` : ''
      }.`
    );
  } else if (blocked > 0) {
    // Blocking with no pencil: the only moves are to leave it out here, or fix it upstream.
    parts.push(
      excluded
        ? `${blocked} ${blocked === 1 ? 'is' : 'are'} being left out of this batch unless fixed elsewhere.`
        : `${blocked} ${blocked === 1 ? 'prints' : 'print'} as ${
            blocked === 1 ? 'it is' : 'they are'
          } unless fixed elsewhere.`
    );
  }

  if (informational > 0) {
    const one = informational === 1;
    // Only claim the lot-less print when it is true of every informational row — several of them
    // (already dispensed, locked for count) print with their lot perfectly well.
    const allWithoutLot = withoutLot === informational;

    parts.push(
      `${informational} ${one ? 'is' : 'are'} listed for information${
        allWithoutLot ? ` — ${one ? 'it prints' : 'they print'} without a lot number.` : '.'
      }`
    );

    if (!allWithoutLot && withoutLot > 0) {
      parts.push(`${withoutLot} of those print without a lot number.`);
    }
  }

  return parts.join(' ');
};

/**
 * The banner's sentence about the rows that were checked and then kept off the grid.
 *
 * Its own function only because it has to agree with itself: the count drives three words in one
 * sentence, and the inline version got two of them wrong for a batch of one — "1 have nothing to
 * change, so they are not listed" was the most common single-item case on the screen.
 *
 * Returns '' at zero rather than a sentence about nothing. "0 have nothing to change" read as a
 * contradiction next to a grid full of rows that could not be changed.
 */
export const autoIncludedSummary = ({ autoIncluded }) => {
  if (!autoIncluded) return '';

  const one = autoIncluded === 1;

  return `${autoIncluded} ${one ? 'has' : 'have'} nothing to change, so ${
    one ? 'it is' : 'they are'
  } not listed. `;
};

/**
 * The banner's opening sentence.
 *
 * Blocked rows print by DEFAULT — the checkbox under the grid is what takes them out — so this
 * has to be asked of that toggle rather than of the classifier. Reading "22 of the 24 will print"
 * off the row statuses, while the Print button below was about to produce 24, was the screen
 * contradicting itself in two places at once.
 */
export const printableSummary = ({ total, blocked, excluded }) => {
  if (excluded && blocked > 0) {
    return `${total - blocked} of the ${total} selected items will print.`;
  }

  return total === 1 ? 'The selected item will print.' : `All ${total} selected items will print.`;
};
